/**
 * useProductRefund Hook
 * Ön ödemeli kart "Satın Alınanlar" listesi ve tekil ürün iadesi
 * (handleOpenPurchaseItems, handleRefundItem) — tamamen SQL (cardManagementService).
 */

import { useState, useCallback } from 'react';
import { cardManagementService } from '../../../../services/cardManagementService';
import { CashierInfo } from '../components/modals/CashierLoginModal';

export interface UseProductRefundParams {
  companyId: string | null;
  prepaidCard: any;
  setPrepaidCard: React.Dispatch<React.SetStateAction<any>>;
  selectedPrepaidWalletId: number | null;
  currentCashier: CashierInfo | null;
  showAlertModal: (title: string, message: string) => void;
}

export interface UseProductRefundReturn {
  // Satın alınanlar (purchase items) state
  isPurchaseItemsOpen: boolean;
  setIsPurchaseItemsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  purchaseItems: any[];
  setPurchaseItems: React.Dispatch<React.SetStateAction<any[]>>;
  purchaseItemsLoading: boolean;
  purchaseItemsGrandTotal: number;
  // İade seçim / onay state
  selectedRefundItem: any;
  setSelectedRefundItem: React.Dispatch<React.SetStateAction<any>>;
  refundLoading: boolean;
  refundConfirmOpen: boolean;
  setRefundConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // Handler'lar
  handleOpenPurchaseItems: () => Promise<void>;
  handleRefundItem: () => Promise<void>;
}

/**
 * İade işlemleri hook'u
 */
export const useProductRefund = ({
  companyId,
  prepaidCard,
  setPrepaidCard,
  selectedPrepaidWalletId,
  currentCashier,
  showAlertModal,
}: UseProductRefundParams): UseProductRefundReturn => {
  const [isPurchaseItemsOpen, setIsPurchaseItemsOpen] = useState(false);
  const [purchaseItems, setPurchaseItems] = useState<any[]>([]);
  const [purchaseItemsLoading, setPurchaseItemsLoading] = useState(false);
  const [purchaseItemsGrandTotal, setPurchaseItemsGrandTotal] = useState(0);
  const [selectedRefundItem, setSelectedRefundItem] = useState<any>(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);

  const handleOpenPurchaseItems = useCallback(async () => {
    if (!prepaidCard?.activeSession || !companyId) {
      showAlertModal("Uyarı", "Aktif bir kart oturumu bulunamadı.");
      return;
    }
    setPurchaseItemsLoading(true);
    setIsPurchaseItemsOpen(true);
    setSelectedRefundItem(null);
    try {
      const res = await cardManagementService.getSessionPurchaseItems(
        prepaidCard.activeSession.id, companyId
      );
      const items = res.data?.data || [];
      setPurchaseItems(items);
      setPurchaseItemsGrandTotal(res.data?.grandTotal || items.reduce((sum: number, i: any) => sum + (i.totalAmount || 0), 0));
    } catch (err) {
      console.error('[SATIN ALINANLAR] Ürünler yüklenirken hata:', err);
      setPurchaseItems([]);
      setPurchaseItemsGrandTotal(0);
    } finally {
      setPurchaseItemsLoading(false);
    }
  }, [prepaidCard, companyId, showAlertModal]);

  const handleRefundItem = useCallback(async () => {
    if (!selectedRefundItem || !prepaidCard?.activeSession || !companyId) return;

    setRefundLoading(true);
    try {
      const walletId = selectedPrepaidWalletId || prepaidCard.wallets?.[0]?.walletId;
      if (!walletId) {
        showAlertModal("Hata", "Karta tanımlı cüzdan bulunamadı.");
        return;
      }

      await cardManagementService.refundPurchaseItem({
        CardId: prepaidCard.id,
        WalletId: walletId,
        ProductId: selectedRefundItem.productId,
        ProductName: selectedRefundItem.productName,
        WarehouseId: selectedRefundItem.warehouseId,
        Quantity: selectedRefundItem.totalQuantity,
        UnitPrice: selectedRefundItem.unitPrice,
        Amount: selectedRefundItem.totalAmount,
        VatRate: selectedRefundItem.vatRate || 0,
        SctRate: selectedRefundItem.sctRate || 0,
        CreatedBy: currentCashier?.cashierName || 'Kasiyer',
        // Satın alma bazlı satır: iade doğru satın almanın gün sonu fişinden düşülsün
        PurchaseReferenceId: selectedRefundItem.referenceId || null,
      });

      setRefundConfirmOpen(false);
      setSelectedRefundItem(null);
      showAlertModal("Başarılı", `${selectedRefundItem.productName} iade edildi.`);

      const res = await cardManagementService.getSessionPurchaseItems(
        prepaidCard.activeSession.id, companyId
      );
      const items = res.data?.data || [];
      setPurchaseItems(items);
      setPurchaseItemsGrandTotal(res.data?.grandTotal || items.reduce((sum: number, i: any) => sum + (i.totalAmount || 0), 0));

      try {
        const cardRes = await cardManagementService.getCardByUID(companyId, prepaidCard.cardUID);
        if (cardRes.data?.success && cardRes.data.data) {
          setPrepaidCard((prev: any) => prev ? ({ ...prev, ...cardRes.data.data }) : prev);
        }
      } catch (e) {
        console.error('[İADE] Kart bakiyesi yenilenemedi:', e);
      }
    } catch (err: any) {
      console.error('[İADE] Hata:', err);
      showAlertModal("Hata", err?.response?.data?.message || "İade işlemi başarısız oldu.");
    } finally {
      setRefundLoading(false);
    }
  }, [selectedRefundItem, prepaidCard, companyId, selectedPrepaidWalletId, currentCashier, showAlertModal, setPrepaidCard]);

  return {
    isPurchaseItemsOpen,
    setIsPurchaseItemsOpen,
    purchaseItems,
    setPurchaseItems,
    purchaseItemsLoading,
    purchaseItemsGrandTotal,
    selectedRefundItem,
    setSelectedRefundItem,
    refundLoading,
    refundConfirmOpen,
    setRefundConfirmOpen,
    handleOpenPurchaseItems,
    handleRefundItem,
  };
};

export default useProductRefund;
